import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react'

import { BACKEND } from './env-variables'
import { JSONreplacer } from './utils-functions'
import {
  sortAlphabetically,
  sortNumerically,
  sortStagioni,
  sortGiornate,
  sortSfide,
  sortTabelloni,
  sortDataOra,
  sortClassifiche,
  sortCodiceSocieta,
  sortTessere,
} from './sort-functions'
import { setOptimisticBackendR } from '../container/home'

const parseBody = body => JSON.parse(JSON.stringify(body, JSONreplacer))

const unquote = value => value?.replace(/^"|"$/g, '')?.trim()

const extractFileName = x => {
  if (!x) return null

  // Extract "filename*" (UTF-8)
  const utf8Match = x.match(/filename\*\s*=\s*([^;]+)/i)?.[1]
  if (utf8Match) {
    const utf8Value = unquote(utf8Match)
    const encodedFileName =
      utf8Value.match(/^[^']*'[^']*'(.*)$/)?.[1] || utf8Value

    try {
      return decodeURIComponent(encodedFileName)
    } catch {
      return encodedFileName
    }
  }

  // Extract "filename" (ASCII)
  const asciiMatch = x.match(/filename\s*=\s*([^;]+)/i)?.[1]
  if (!asciiMatch) return null
  return unquote(asciiMatch)
}

export const apiSlice = createApi({
  reducerPath: 'api',
  baseQuery: fetchBaseQuery({
    baseUrl: BACKEND,
    prepareHeaders: (headers, { getState }) => {
      const state = getState()

      const token = state?.home?.infoUtente?.token
      if (token) headers.set('Authorization', `Bearer ${token}`)

      return headers
    },
  }),
  tagTypes: [
    'Accoppiamenti',
    'Affiliazioni',
    'Arbitraggi',
    'Arbitri',
    'Calendari',
    'Campionati',
    'Categorie',
    'Classifiche',
    'Corsi',
    'Designazioni',
    'Distinte',
    'Domeniche',
    'Impostazioni',
    'Iscrizioni',
    'Iscrizioni-Corsi',
    'Rendiconti',
    'Rimborsi',
    'Spostamenti',
    'Tabelloni',
    'Tesserati',
    'Zone',
  ],
  endpoints: builder => ({
    // accoppiamenti
    getAccoppiamenti: builder.query({
      query: () => 'accoppiamenti/all',
      providesTags: ['Accoppiamenti'],
      transformResponse: response =>
        response?.sort(
          (a, b) =>
            sortNumerically(a.gironiDa, b.gironiDa) ||
            sortGiornate(a.giornata, b.giornata),
        ),
    }),
    createAccoppiamento: builder.mutation({
      query: body => ({ url: 'accoppiamenti/add', method: 'POST', body }),
      invalidatesTags: ['Accoppiamenti'],
    }),
    updateAccoppiamento: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `accoppiamenti/${id}`,
        method: 'POST',
        body: parseBody(body),
      }),
      invalidatesTags: ['Accoppiamenti'],
    }),
    deleteAccoppiamento: builder.mutation({
      query: id => ({ url: `accoppiamenti/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Accoppiamenti'],
    }),

    // affiliazioni
    getAffiliazioni: builder.query({
      query: () => 'affiliazioni/all',
      providesTags: ['Affiliazioni'],
      transformResponse: response =>
        response?.map(a => ({
          ...a,
          codiceSocieta: Number(a.codiceSocieta),
          status: a.isActive ? a.status : 'inactive',
        })),
    }),
    getAffiliazioniLight: builder.query({
      query: () => 'affiliazioni/light',
      providesTags: ['Affiliazioni'],
      transformResponse: response =>
        response?.map(a => ({ ...a, codiceSocieta: Number(a.codiceSocieta) })),
    }),
    getAffiliazioneById: builder.query({
      query: id => `affiliazioni/get/${id}`,
      transformResponse: response => response?.data,
    }),
    getAffiliazioneByCodice: builder.query({
      query: codiceSocieta => `affiliazioni/codice/${codiceSocieta}`,
      transformResponse: response => response?.data,
    }),
    getAffiliazioniStats: builder.query({
      query: () => 'affiliazioni/stats',
      providesTags: ['Affiliazioni'],
    }),
    createAffiliazione: builder.mutation({
      query: body => ({ url: 'affiliazioni/add', method: 'POST', body }),
      invalidatesTags: ['Affiliazioni'],
    }),
    updateAffiliazione: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `affiliazioni/${id}`,
        method: 'PUT',
        body: parseBody(body),
      }),
      invalidatesTags: ['Affiliazioni'],
    }),
    deleteAffiliazione: builder.mutation({
      query: id => ({ url: `affiliazioni/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Affiliazioni'],
    }),

    // algoritmi
    algoritmiCheckFolder: builder.mutation({
      query: algorithm => `algoritmi/checkfolder/${algorithm}`,
    }),
    algoritmiCheckFile: builder.mutation({
      query: ({ algorithm, type, fileName }) =>
        `algoritmi/checkfile/${algorithm}/${type}/${fileName}`,
    }),
    algoritmiUpload: builder.mutation({
      query: ({ algorithm, fileName, formData }) => ({
        url: `algoritmi/upload/${algorithm}${fileName ? `/${fileName}` : ''}`,
        method: 'POST',
        body: formData,
      }),
    }),
    algoritmiRun: builder.mutation({
      query: ({ algorithm, urlSuffix }) => ({
        url: `algoritmi/run/${algorithm}${urlSuffix}`,
      }),
    }),
    algoritmiExcel: builder.mutation({
      async queryFn(
        { urlSuffix, body, urlPrefix = 'excel/' },
        _queryApi,
        _extraOptions,
        fetchWithBQ,
      ) {
        const result = await fetchWithBQ({
          url: `algoritmi/${urlPrefix}${urlSuffix}`,
          method: body ? 'POST' : 'GET',
          body,
          responseHandler: response => response.blob(),
        })

        if (result?.error)
          return { error: { message: 'Impossibile scaricare il file.' } }

        const resHeaders = result?.meta?.response?.headers
        const contentDisposition = resHeaders?.get('Content-Disposition')
        const fileName = extractFileName(contentDisposition)

        return { data: { blob: result?.data, fileName } }
      },
    }),

    // arbitraggi
    getArbitraggi: builder.query({
      query: ({ selectedCampionato, admin, designatore, codiceUtente }) => {
        const baseUrl = `arbitraggi/campionato/${selectedCampionato}`
        const fSocieta = admin || designatore ? '' : `/societa/${codiceUtente}`

        return `${baseUrl}${fSocieta}`
      },
      providesTags: ['Arbitraggi'],
      transformResponse: response =>
        response
          ?.map(a => ({ ...a, codiceSocieta: Number(a.codiceSocieta) }))
          ?.reverse(),
    }),
    getArbitraggiStats: builder.query({
      query: campionato => `arbitraggi/stats/campionato/${campionato}`,
      providesTags: ['Arbitraggi'],
    }),
    createArbitraggio: builder.mutation({
      query: body => ({ url: 'arbitraggi/add', method: 'POST', body }),
      invalidatesTags: ['Arbitraggi', 'Calendari'],
    }),
    deleteArbitraggio: builder.mutation({
      query: ({ codiceCampionato, numeroGara }) => ({
        url: `arbitraggi/campionato/${codiceCampionato}/numero-gara/${numeroGara}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Arbitraggi', 'Calendari', 'Designazioni'],
    }),

    // arbitri
    getArbitri: builder.query({
      query: () => 'arbitri/all',
      providesTags: ['Arbitri'],
      transformResponse: response =>
        response?.map(a => ({
          ...a,
          status: a.isActive ? a.status : 'inactive',
        })),
    }),
    getArbitriLight: builder.query({
      query: () => 'arbitri/light',
      providesTags: ['Arbitri'],
    }),
    getArbitroById: builder.query({
      query: id => `arbitri/${id}`,
      providesTags: ['Arbitri'],
      transformResponse: response => response?.data,
    }),
    getArbitriStats: builder.query({
      query: () => 'arbitri/stats',
      providesTags: ['Arbitri'],
    }),
    createArbitro: builder.mutation({
      query: body => ({ url: 'arbitri/add', method: 'POST', body }),
      invalidatesTags: ['Arbitri'],
    }),
    updateArbitro: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `arbitri/${id}`,
        method: 'PUT',
        body: parseBody(body),
      }),
      invalidatesTags: ['Arbitri'],
    }),
    updateArbitroAutocertificazione: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `arbitri/${id}/autocertificazione`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Arbitri'],
    }),
    deleteArbitro: builder.mutation({
      query: id => ({ url: `arbitri/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Arbitri'],
    }),

    // backend features
    getBackendFeatures: builder.query({
      query: () => 'impostazioni/features',
    }),

    // backend R
    getBackendR: builder.query({
      query: () => 'backendR/all',
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
        } catch {
        } finally {
          dispatch(setOptimisticBackendR())
        }
      },
    }),

    // calendari
    getCalendari: builder.query({
      query: ({ selectedCampionato, codiceUtente, tipologia, admin }) => {
        const baseUrl = `calendari/campionato/${selectedCampionato}`
        if (admin && ['arbitraggio', 'spostamento'].includes(tipologia))
          return baseUrl
        if (codiceUtente && tipologia)
          return `${baseUrl}/societa/${codiceUtente}/${tipologia}`
        return baseUrl
      },
      providesTags: ['Calendari'],
    }),
    getCalendariReferee: builder.query({
      query: selectedCampionato =>
        `calendari/campionato/${selectedCampionato}/referee`,
      providesTags: ['Calendari'],
    }),
    getCalendariRefereeData: builder.query({
      query: body => ({ url: 'calendari/referee/data', method: 'POST', body }),
      providesTags: ['Calendari'],
    }),
    getCalendariFinali: builder.query({
      query: selectedCampionato =>
        `calendari/campionato/${selectedCampionato}/girone/finali`,
      providesTags: ['Calendari'],
      transformResponse: response =>
        response?.sort((a, b) => sortSfide(a.sfidaTabellone, b.sfidaTabellone)),
    }),
    getCalendariMany: builder.query({
      query: ids => ({ url: 'calendari/many', method: 'POST', body: { ids } }),
      providesTags: ['Calendari'],
    }),
    getCalendariProvvedimenti: builder.query({
      query: ({ selectedCampionato, selectedProvvedimento }) =>
        `calendari/${selectedProvvedimento}/campionato/${selectedCampionato}`,
      providesTags: ['Calendari', 'Impostazioni'],
      transformResponse: response =>
        response?.sort((a, b) => sortDataOra(a.data, a.ora, b.data, b.ora)),
    }),
    getCalendariBySquadra: builder.query({
      query: id => `calendari/team/${id}`,
      providesTags: ['Calendari'],
    }),
    getCalendariBySfida: builder.mutation({
      query: ({ codiceCampionato, categoria, sfidaTabellone }) =>
        `calendari/campionato/${codiceCampionato}/categoria/${categoria}/sfida/${sfidaTabellone}`,
      providesTags: ['Calendari'],
    }),
    getCalendarioById: builder.query({
      query: id => `calendari/${id}`,
      providesTags: ['Calendari'],
      transformResponse: response => response?.data,
    }),
    getCalendariStats: builder.query({
      query: campionato => `calendari/stats/campionato/${campionato}`,
      providesTags: ['Calendari'],
    }),
    getCalendariFinaliStats: builder.query({
      query: campionato =>
        `calendari/categorie/campionato/${campionato}/finali`,
      providesTags: ['Calendari'],
    }),
    getCalendariProvvedimentiStats: builder.query({
      query: ({ selectedProvvedimento, selectedCampionato }) =>
        `calendari/${selectedProvvedimento}/campionato/${selectedCampionato}`,
      providesTags: ['Calendari'],
      transformResponse: response => response?.length,
    }),
    createCalendario: builder.mutation({
      query: body => ({ url: 'calendari/add', method: 'POST', body }),
      invalidatesTags: ['Calendari'],
    }),
    updateCalendario: builder.mutation({
      query: ({ id, tipologia, ...body }) => ({
        url: `calendari/${tipologia}/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Calendari', 'Classifiche', 'Designazioni'],
    }),
    updateCalendarioDataOra: builder.mutation({
      query: ({ id, tipologia, ...body }) => ({
        url: `calendari/${id}/${tipologia}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: [
        'Arbitraggi',
        'Calendari',
        'Designazioni',
        'Rendiconti',
      ],
    }),
    updateCalendarioIndirizzo: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `calendari/${id}/indirizzo`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Calendari'],
    }),
    updateCalendarioSanzioneReferto: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `calendari/sanzione-referto/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Calendari'],
    }),
    deleteCalendario: builder.mutation({
      query: id => ({ url: `calendari/${id}`, method: 'DELETE' }),
      invalidatesTags: [
        'Arbitraggi',
        'Calendari',
        'Classifiche',
        'Designazioni',
        'Spostamenti',
      ],
    }),
    deleteCalendariByTeam: builder.mutation({
      query: id => ({ url: `calendari/team/${id}`, method: 'DELETE' }),
      invalidatesTags: [
        'Arbitraggi',
        'Calendari',
        'Classifiche',
        'Designazioni',
        'Spostamenti',
      ],
    }),

    // campionati
    getCampionati: builder.query({
      query: () => 'campionati/all',
      providesTags: ['Campionati'],
      transformResponse: response =>
        response?.sort((a, b) => b.indice - a.indice),
    }),
    createCampionato: builder.mutation({
      query: body => ({ url: 'campionati/add', method: 'POST', body }),
      invalidatesTags: ['Campionati'],
    }),
    updateCampionato: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `campionati/${id}`,
        method: 'PUT',
        body: parseBody(body),
      }),
      invalidatesTags: ['Campionati'],
    }),
    deleteCampionato: builder.mutation({
      query: id => ({ url: `campionati/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Campionati'],
    }),
    getCurrentSeasonRange: builder.query({
      query: () => 'campionati/range/stagione/corrente',
    }),

    // categorie
    getCategorie: builder.query({
      query: () => 'categorie/all',
      providesTags: ['Categorie'],
      transformResponse: response =>
        response?.sort(
          (a, b) =>
            b.isActive - a.isActive || sortAlphabetically(a.nome, b.nome),
        ),
    }),
    createCategoria: builder.mutation({
      query: body => ({ url: 'categorie/add', method: 'POST', body }),
      invalidatesTags: ['Categorie'],
    }),
    updateCategoria: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `categorie/${id}`,
        method: 'PUT',
        body: parseBody(body),
      }),
      invalidatesTags: ['Categorie'],
    }),
    deleteCategoria: builder.mutation({
      query: id => ({ url: `categorie/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Categorie'],
    }),

    // classifiche
    getClassifiche: builder.query({
      query: selectedCampionato =>
        `classifiche/campionato/${selectedCampionato}`,
      providesTags: ['Classifiche'],
      transformResponse: response => response?.sort(sortClassifiche),
    }),

    // corsi
    getCorsi: builder.query({
      query: () => 'corsi/all',
      providesTags: ['Corsi'],
      transformResponse: response =>
        response?.sort(
          (a, b) =>
            sortStagioni(a.stagione, b.stagione) ||
            sortAlphabetically(a.nome, b.nome),
        ),
    }),
    createCorso: builder.mutation({
      query: body => ({ url: 'corsi/add', method: 'POST', body }),
      invalidatesTags: ['Corsi'],
    }),
    updateCorso: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `corsi/${id}`,
        method: 'PUT',
        body: parseBody(body),
      }),
      invalidatesTags: ['Corsi'],
    }),
    deleteCorso: builder.mutation({
      query: id => ({ url: `corsi/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Corsi'],
    }),

    // designazioni
    getDesignazioni: builder.query({
      query: urlSuffix => `designazioni${urlSuffix}`,
      providesTags: ['Designazioni'],
      transformResponse: response => response?.reverse(),
    }),
    getDesignazioniAccepted: builder.query({
      query: selectedCampionato =>
        `designazioni/campionato/${selectedCampionato}/accepted`,
      providesTags: ['Designazioni'],
    }),
    getDesignazioniByArbitro: builder.query({
      query: idArbitro => `designazioni/arbitro/${idArbitro}`,
      providesTags: ['Designazioni'],
    }),
    getDesignazioniByArbitroRimborso: builder.query({
      query: ({ idArbitro, urlSuffix }) =>
        `designazioni/arbitro/${idArbitro}/rimborso${urlSuffix}`,
      providesTags: ['Designazioni', 'Rimborsi'],
      transformResponse: response =>
        response?.sort((a, b) => sortDataOra(a.data, a.ora, b.data, b.ora)),
    }),
    getDesignazioniByGara: builder.query({
      query: id => `designazioni/gara/${id}`,
      providesTags: ['Designazioni'],
    }),
    getDesignazioniByGaraOther: builder.query({
      query: ({ idGara, idArbitro }) =>
        `designazioni/gara/${idGara}/other/${idArbitro}`,
      providesTags: ['Designazioni'],
      transformResponse: response =>
        response?.length === 1 ? response[0] : undefined,
    }),
    getDesignazioniStats: builder.query({
      query: ({ stagione, campionato, idArbitro }) => {
        const baseUrl = 'designazioni/stats'
        if (campionato) return `${baseUrl}/campionato/${campionato}`
        return `${baseUrl}${stagione}/arbitro/${idArbitro}`
      },
      providesTags: ['Designazioni'],
    }),
    createDesignazione: builder.mutation({
      query: body => ({ url: 'designazioni/add', method: 'POST', body }),
      invalidatesTags: ['Designazioni'],
    }),
    annullaDesignazione: builder.mutation({
      query: body => ({ url: 'designazioni/annulla', method: 'PATCH', body }),
      invalidatesTags: ['Designazioni'],
    }),
    updateDesignazioneReply: builder.mutation({
      query: ({ id, reply }) => ({
        url: `designazioni/${id}/${reply}`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Designazioni'],
    }),
    deleteDesignazioniByArbitro: builder.mutation({
      query: id => ({ url: `designazioni/arbitro/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Designazioni'],
    }),

    // distinte
    getDistinte: builder.query({
      query: ({ admin, selectedCampionato, codiceUtente }) => {
        const baseUrl = `distinte/campionato/${selectedCampionato}`
        if (admin) return baseUrl
        return `${baseUrl}/societa/${codiceUtente}`
      },
      providesTags: ['Distinte'],
      transformResponse: response => response?.reverse(),
    }),
    getLastDistintaByTeam: builder.query({
      query: idSquadra => `distinte/last/team/${idSquadra}`,
      providesTags: ['Distinte'],
    }),
    getDistinteStats: builder.query({
      query: ({ campionato, codiceUtente }) => {
        const baseUrl = `distinte/stats/campionato/${campionato}`
        if (codiceUtente) return `${baseUrl}/societa/${codiceUtente}`
        return baseUrl
      },
      providesTags: ['Distinte'],
    }),
    createDistinta: builder.mutation({
      query: body => ({ url: 'distinte/add', method: 'POST', body }),
      invalidatesTags: ['Distinte'],
    }),
    updateDistinta: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `distinte/${id}`,
        method: 'PATCH',
        body: parseBody(body),
      }),
      invalidatesTags: ['Distinte'],
    }),
    deleteDistinta: builder.mutation({
      query: id => ({ url: `distinte/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Distinte'],
    }),

    // domeniche
    getDomeniche: builder.query({
      query: () => 'domeniche/all',
      providesTags: ['Domeniche'],
      transformResponse: response =>
        response?.sort((a, b) => sortGiornate(a.giornata, b.giornata)),
    }),
    createDomenica: builder.mutation({
      query: body => ({ url: 'domeniche/add', method: 'POST', body }),
      invalidatesTags: ['Domeniche'],
    }),
    updateDomenica: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `domeniche/${id}`,
        method: 'PUT',
        body: parseBody(body),
      }),
      invalidatesTags: ['Domeniche'],
    }),
    deleteDomenica: builder.mutation({
      query: id => ({ url: `domeniche/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Domeniche'],
    }),

    // impostazioni
    getImpostazioniAlgoritmi: builder.query({
      query: () => 'impostazioni/get/algoritmi',
      providesTags: ['Impostazioni'],
    }),
    getImpostazioniAvanzate: builder.query({
      query: fields => {
        const baseUrl = 'impostazioni/get/avanzate'
        if (Array.isArray(fields))
          return `${baseUrl}?fields=${fields.join(',')}`
        return baseUrl
      },
      providesTags: ['Impostazioni'],
    }),
    getImpostazioniContatti: builder.query({
      query: () => 'impostazioni/get/contatti',
      providesTags: ['Impostazioni'],
    }),
    getImpostazioniMail: builder.query({
      query: () => 'impostazioni/get/mail',
      providesTags: ['Impostazioni'],
    }),
    updateImpostazioni: builder.mutation({
      query: body => ({ url: 'impostazioni/update', method: 'PUT', body }),
      invalidatesTags: ['Impostazioni'],
    }),
    login: builder.mutation({
      query: body => ({ url: 'impostazioni/login', method: 'POST', body }),
    }),
    autoLogin: builder.query({
      query: token => ({
        url: 'impostazioni/autologin',
        headers: { Authorization: `Bearer ${token}` },
      }),
    }),
    changePassword: builder.mutation({
      query: body => ({
        url: 'impostazioni/change-password',
        method: 'POST',
        body,
      }),
    }),
    resetPasswordRequest: builder.mutation({
      query: body => ({
        url: 'impostazioni/reset-password-request',
        method: 'POST',
        body,
      }),
    }),
    resetPassword: builder.mutation({
      query: body => ({
        url: 'impostazioni/reset-password',
        method: 'POST',
        body,
      }),
    }),
    impersonate: builder.query({
      query: codiceUtente => `impostazioni/impersonate/${codiceUtente}`,
    }),
    testMail: builder.mutation({
      query: body => ({ url: 'mail/test', method: 'POST', body }),
    }),
    testGoogleMaps: builder.mutation({
      query: body => ({
        url: 'impostazioni/test-google-maps',
        method: 'POST',
        body,
      }),
    }),

    // iscrizioni
    getIscrizioni: builder.query({
      query: ({
        auth,
        stagione,
        campionato,
        categoria,
        codiceSocieta,
        iFilter,
      }) => {
        if (stagione) return `iscrizioni/stagione/${stagione}/${iFilter}`
        const baseUrl = `iscrizioni/campionato/${campionato}`
        if (auth) return baseUrl
        if (iFilter) return `${baseUrl}/${iFilter}`
        if (categoria) return `${baseUrl}/categoria/${categoria}`
        if (codiceSocieta) return `${baseUrl}/societa/${codiceSocieta}`
        return baseUrl
      },
      providesTags: ['Iscrizioni'],
      transformResponse: response =>
        response
          ?.map(i => ({ ...i, codiceSocieta: Number(i.codiceSocieta) }))
          ?.reverse(),
    }),
    getIscrizioniBySocieta: builder.query({
      query: codiceSocieta => `iscrizioni/societa/${codiceSocieta}`,
      providesTags: ['Iscrizioni'],
    }),
    getIscrizioneById: builder.query({
      query: id => `iscrizioni/${id}`,
      providesTags: ['Iscrizioni'],
      transformResponse: response => response?.data,
    }),
    getIscrizioniMany: builder.query({
      query: ids => ({ url: 'iscrizioni/many', method: 'POST', body: { ids } }),
      providesTags: ['Iscrizioni'],
    }),
    getIscrizioniStats: builder.query({
      query: campionato => `iscrizioni/stats/campionato/${campionato}`,
      providesTags: ['Iscrizioni'],
    }),
    createIscrizione: builder.mutation({
      query: body => ({ url: 'iscrizioni/add', method: 'POST', body }),
      invalidatesTags: ['Iscrizioni'],
    }),
    updateIscrizione: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `iscrizioni/${id}`,
        method: 'PUT',
        body: parseBody(body),
      }),
      invalidatesTags: ['Classifiche', 'Iscrizioni', 'Rendiconti'],
    }),
    updateIscrizioneGirone: builder.mutation({
      query: ({ id, girone }) => ({
        url: `iscrizioni/${id}/girone`,
        method: 'PATCH',
        body: { girone },
      }),
      invalidatesTags: ['Iscrizioni'],
    }),
    updateIscrizionePosizione: builder.mutation({
      query: ({ id, posizione }) => ({
        url: `iscrizioni/${id}/posizione`,
        method: 'PATCH',
        body: { posizione },
      }),
      invalidatesTags: ['Iscrizioni'],
    }),
    deleteIscrizione: builder.mutation({
      query: id => ({ url: `iscrizioni/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Iscrizioni'],
    }),
    deleteIscrizioniBySocieta: builder.mutation({
      query: codiceSocieta => ({
        url: `iscrizioni/societa/${codiceSocieta}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Calendari', 'Classifiche', 'Iscrizioni'],
    }),

    // iscrizioni-corsi
    getIscrizioniCorsi: builder.query({
      query: ({ admin, arbitro, idArbitro, codiceUtente }) => {
        const baseUrl = 'iscrizioni-corsi'
        if (admin) return `${baseUrl}/all`
        if (arbitro) return `${baseUrl}/arbitro/${idArbitro}`
        return `${baseUrl}/societa/${codiceUtente}`
      },
      providesTags: ['Iscrizioni-Corsi'],
      transformResponse: response =>
        response
          ?.map(i => ({
            ...i,
            codiceSocieta: Number(i.codiceSocieta) || undefined,
          }))
          ?.reverse(),
    }),
    getIscrizioniCorsiStats: builder.query({
      query: stagione => `iscrizioni-corsi/stats/stagione/${stagione}`,
      providesTags: ['Iscrizioni-Corsi'],
    }),
    getIscrizioneCorsiById: builder.query({
      query: id => `iscrizioni-corsi/${id}`,
      transformResponse: response => response?.data,
    }),
    createIscrizioneCorsi: builder.mutation({
      query: body => ({
        url: 'iscrizioni-corsi/add',
        method: 'POST',
        body: parseBody(body),
      }),
      invalidatesTags: ['Iscrizioni-Corsi'],
    }),
    updateIscrizioneCorsi: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `iscrizioni-corsi/${id}`,
        method: 'PUT',
        body: parseBody(body),
      }),
      invalidatesTags: ['Iscrizioni-Corsi'],
    }),
    deleteIscrizioneCorsi: builder.mutation({
      query: id => ({ url: `iscrizioni-corsi/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Iscrizioni-Corsi'],
    }),

    // rendiconti
    getRendiconti: builder.query({
      query: urlSuffix => `rendiconti${urlSuffix}`,
      providesTags: ['Rendiconti'],
      transformResponse: response =>
        response
          ?.map(r => ({ ...r, codiceSocieta: Number(r.codiceSocieta) }))
          ?.sort((a, b) => sortStagioni(a, b) || sortCodiceSocieta(a, b)),
    }),
    getRendicontiStats: builder.query({
      query: stagione => `rendiconti/stats/stagione/${stagione}`,
      providesTags: ['Rendiconti'],
    }),
    createRendiconto: builder.mutation({
      query: body => ({ url: 'rendiconti/add', method: 'POST', body }),
      invalidatesTags: ['Calendari', 'Rendiconti'],
    }),
    updateRendiconto: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `rendiconti/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Rendiconti'],
    }),
    upgradeRendiconto: builder.mutation({
      query: id => ({ url: `rendiconti/${id}`, method: 'PUT' }),
      invalidatesTags: ['Calendari', 'Rendiconti'],
    }),

    // rimborsi
    getRimborsi: builder.query({
      query: urlSuffix => `rimborsi${urlSuffix}`,
      providesTags: ['Rimborsi'],
      transformResponse: response => response?.reverse(),
    }),
    getRimborsiStats: builder.query({
      query: ({ stagione, idArbitro }) => {
        const baseUrl = 'rimborsi/stats'
        const fStagione = `/stagione/${stagione}`
        if (!idArbitro) return `${baseUrl}${fStagione}`
        return `${baseUrl}/arbitro/${idArbitro}${fStagione}`
      },
      providesTags: ['Rimborsi'],
    }),
    createRimborso: builder.mutation({
      query: body => ({ url: 'rimborsi/add', method: 'POST', body }),
      invalidatesTags: ['Rimborsi'],
    }),
    updateRimborso: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `rimborsi/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Rimborsi'],
    }),
    updateRimborsoIsPaid: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `rimborsi/is-paid/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Rimborsi'],
    }),
    deleteRimborso: builder.mutation({
      query: id => ({ url: `rimborsi/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Rimborsi'],
    }),

    // spostamenti
    getSpostamenti: builder.query({
      query: ({ selectedCampionato, codiceUtente, admin }) => {
        const baseUrl = `spostamenti/campionato/${selectedCampionato}`
        if (admin) return baseUrl
        return `${baseUrl}/societa/${codiceUtente}`
      },
      providesTags: ['Spostamenti'],
      transformResponse: response => response?.reverse(),
    }),
    getSpostamentiBySocieta: builder.query({
      query: codiceSocieta => `spostamenti/societa/${codiceSocieta}`,
      providesTags: ['Spostamenti'],
    }),
    getSpostamentiWaiting: builder.query({
      query: selectedCampionato =>
        `spostamenti/campionato/${selectedCampionato}/waiting`,
      providesTags: ['Spostamenti'],
    }),
    getSpostamentoById: builder.query({
      query: id => `spostamenti/${id}`,
      transformResponse: response => response?.data,
    }),
    getSpostamentiStats: builder.query({
      query: campionato => `spostamenti/stats/campionato/${campionato}`,
      providesTags: ['Spostamenti'],
    }),
    getSpostamentiStatsBySocieta: builder.query({
      query: ({ selectedCampionato, codiceUtente }) =>
        `spostamenti/stats/campionato/${selectedCampionato}/societa/${codiceUtente}`,
      providesTags: ['Spostamenti'],
    }),
    createSpostamento: builder.mutation({
      query: body => ({ url: 'spostamenti/add', method: 'POST', body }),
      invalidatesTags: ['Calendari', 'Spostamenti'],
    }),
    updateSpostamentoReply: builder.mutation({
      query: ({ id, reply }) => ({
        url: `spostamenti/${id}/${reply}`,
        method: 'PATCH',
      }),
      invalidatesTags: ['Calendari', 'Spostamenti'],
    }),

    // tabelloni
    getTabelloni: builder.query({
      query: () => 'tabelloni/all',
      providesTags: ['Tabelloni'],
      transformResponse: response =>
        response?.sort((a, b) =>
          sortTabelloni(a.squadre, b.squadre, a.nome, b.nome),
        ),
    }),
    createTabellone: builder.mutation({
      query: body => ({ url: 'tabelloni/add', method: 'POST', body }),
      invalidatesTags: ['Tabelloni'],
    }),
    updateTabellone: builder.mutation({
      query: ({ id, ...body }) => ({
        url: `tabelloni/${id}`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Tabelloni'],
    }),
    deleteTabellone: builder.mutation({
      query: id => ({ url: `tabelloni/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Tabelloni'],
    }),
    createTabelloneSfida: builder.mutation({
      query: ({ selectedTabelloneId, ...body }) => ({
        url: `tabelloni/${selectedTabelloneId}/sfida/add`,
        method: 'POST',
        body,
      }),
      invalidatesTags: ['Tabelloni'],
    }),
    updateTabelloneSfida: builder.mutation({
      query: ({ selectedTabelloneId, id, ...body }) => ({
        url: `tabelloni/${selectedTabelloneId}/sfida/${id}`,
        method: 'PATCH',
        body: parseBody(body),
      }),
      invalidatesTags: ['Tabelloni'],
    }),
    deleteTabelloneSfida: builder.mutation({
      query: ({ selectedTabelloneId, id }) => ({
        url: `tabelloni/${selectedTabelloneId}/sfida/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Tabelloni'],
    }),

    // tesserati
    getTesserati: builder.query({
      query: ({ admin, stagione, codiceUtente }) => {
        const baseUrl = `tesserati/stagione/${stagione}`
        if (admin) return baseUrl
        return `${baseUrl}/societa/${codiceUtente}`
      },
      providesTags: ['Tesserati'],
      transformResponse: response =>
        response
          ?.map(a => ({
            ...a,
            cognomenome: `${a.cognome} ${a.nome}`,
            codiceSocieta: Number(a.codiceSocieta),
          }))
          ?.sort(
            (a, b) =>
              sortTessere(a.ruolo, b.ruolo) ||
              sortAlphabetically(a.cognome, b.cognome) ||
              sortAlphabetically(a.nome, b.nome),
          ),
    }),
    getTesseratiPlayersByGaraSquadra: builder.query({
      query: ({ idGara, idSquadra }) =>
        `tesserati/players/gara/${idGara}/squadra/${idSquadra}`,
      providesTags: ['Tesserati'],
    }),
    getTesseratiStats: builder.query({
      query: ({ stagione, codiceUtente }) => {
        const baseUrl = `tesserati/stats/stagione/${stagione}`
        if (codiceUtente) return `${baseUrl}/societa/${codiceUtente}`
        return baseUrl
      },
      providesTags: ['Tesserati'],
    }),

    // zone
    getZone: builder.query({
      query: () => 'zone/all',
      providesTags: ['Zone'],
      transformResponse: response =>
        response?.sort((a, b) => (a.zona > b.zona ? 1 : -1)),
    }),
    createZona: builder.mutation({
      query: body => ({ url: 'zone/add', method: 'POST', body }),
      invalidatesTags: ['Zone'],
    }),
    updateZona: builder.mutation({
      query: ({ id, ...body }) => ({ url: `zone/${id}`, method: 'PUT', body }),
      invalidatesTags: ['Zone'],
    }),
    deleteZona: builder.mutation({
      query: id => ({ url: `zone/${id}`, method: 'DELETE' }),
      invalidatesTags: ['Zone'],
    }),
  }),
})

export const {
  useGetAccoppiamentiQuery,
  useCreateAccoppiamentoMutation,
  useUpdateAccoppiamentoMutation,
  useDeleteAccoppiamentoMutation,

  useGetAffiliazioniQuery,
  useGetAffiliazioniLightQuery,
  useLazyGetAffiliazioneByIdQuery,
  useLazyGetAffiliazioneByCodiceQuery,
  useGetAffiliazioniStatsQuery,
  useCreateAffiliazioneMutation,
  useUpdateAffiliazioneMutation,
  useDeleteAffiliazioneMutation,

  useAlgoritmiCheckFolderMutation,
  useAlgoritmiCheckFileMutation,
  useAlgoritmiUploadMutation,
  useAlgoritmiRunMutation,
  useAlgoritmiExcelMutation,

  useGetArbitraggiQuery,
  useGetArbitraggiStatsQuery,
  useCreateArbitraggioMutation,
  useDeleteArbitraggioMutation,

  useGetArbitriQuery,
  useGetArbitriLightQuery,
  useGetArbitroByIdQuery,
  useLazyGetArbitroByIdQuery,
  useGetArbitriStatsQuery,
  useCreateArbitroMutation,
  useUpdateArbitroMutation,
  useUpdateArbitroAutocertificazioneMutation,
  useDeleteArbitroMutation,

  useGetBackendFeaturesQuery,

  useGetBackendRQuery,
  useLazyGetBackendRQuery,

  useGetCalendariQuery,
  useGetCalendariRefereeQuery,
  useGetCalendariManyQuery,
  useGetCalendariRefereeDataQuery,
  useGetCalendariFinaliQuery,
  useGetCalendariProvvedimentiQuery,
  useGetCalendariBySquadraQuery,
  useGetCalendariBySfidaMutation,
  useGetCalendarioByIdQuery,
  useGetCalendariStatsQuery,
  useGetCalendariFinaliStatsQuery,
  useGetCalendariProvvedimentiStatsQuery,
  useCreateCalendarioMutation,
  useUpdateCalendarioMutation,
  useUpdateCalendarioDataOraMutation,
  useUpdateCalendarioIndirizzoMutation,
  useUpdateCalendarioSanzioneRefertoMutation,
  useDeleteCalendarioMutation,
  useDeleteCalendariByTeamMutation,

  useGetCampionatiQuery,
  useCreateCampionatoMutation,
  useUpdateCampionatoMutation,
  useDeleteCampionatoMutation,
  useGetCurrentSeasonRangeQuery,

  useGetCategorieQuery,
  useCreateCategoriaMutation,
  useUpdateCategoriaMutation,
  useDeleteCategoriaMutation,

  useGetClassificheQuery,

  useGetCorsiQuery,
  useCreateCorsoMutation,
  useUpdateCorsoMutation,
  useDeleteCorsoMutation,

  useGetDesignazioniQuery,
  useGetDesignazioniAcceptedQuery,
  useGetDesignazioniByArbitroQuery,
  useGetDesignazioniByArbitroRimborsoQuery,
  useGetDesignazioniByGaraQuery,
  useGetDesignazioniByGaraOtherQuery,
  useGetDesignazioniStatsQuery,
  useCreateDesignazioneMutation,
  useAnnullaDesignazioneMutation,
  useUpdateDesignazioneReplyMutation,
  useDeleteDesignazioniByArbitroMutation,

  useGetDistinteQuery,
  useLazyGetLastDistintaByTeamQuery,
  useGetDistinteStatsQuery,
  useCreateDistintaMutation,
  useUpdateDistintaMutation,
  useDeleteDistintaMutation,

  useGetDomenicheQuery,
  useCreateDomenicaMutation,
  useUpdateDomenicaMutation,
  useDeleteDomenicaMutation,

  useGetImpostazioniAlgoritmiQuery,
  useGetImpostazioniAvanzateQuery,
  useGetImpostazioniContattiQuery,
  useGetImpostazioniMailQuery,
  useUpdateImpostazioniMutation,
  useLoginMutation,
  useLazyAutoLoginQuery,
  useChangePasswordMutation,
  useResetPasswordRequestMutation,
  useResetPasswordMutation,
  useLazyImpersonateQuery,
  useTestMailMutation,
  useTestGoogleMapsMutation,

  useGetIscrizioniQuery,
  useGetIscrizioniBySocietaQuery,
  useGetIscrizioniManyQuery,
  useGetIscrizioneByIdQuery,
  useLazyGetIscrizioneByIdQuery,
  useGetIscrizioniStatsQuery,
  useCreateIscrizioneMutation,
  useUpdateIscrizioneMutation,
  useUpdateIscrizioneGironeMutation,
  useUpdateIscrizionePosizioneMutation,
  useDeleteIscrizioneMutation,
  useDeleteIscrizioniBySocietaMutation,

  useGetIscrizioniCorsiQuery,
  useGetIscrizioniCorsiStatsQuery,
  useLazyGetIscrizioneCorsiByIdQuery,
  useCreateIscrizioneCorsiMutation,
  useUpdateIscrizioneCorsiMutation,
  useDeleteIscrizioneCorsiMutation,

  useGetRendicontiQuery,
  useGetRendicontiStatsQuery,
  useCreateRendicontoMutation,
  useUpdateRendicontoMutation,
  useUpgradeRendicontoMutation,

  useGetRimborsiQuery,
  useGetRimborsiStatsQuery,
  useCreateRimborsoMutation,
  useUpdateRimborsoMutation,
  useUpdateRimborsoIsPaidMutation,
  useDeleteRimborsoMutation,

  useGetSpostamentiQuery,
  useGetSpostamentiBySocietaQuery,
  useGetSpostamentiWaitingQuery,
  useLazyGetSpostamentoByIdQuery,
  useGetSpostamentiStatsQuery,
  useGetSpostamentiStatsBySocietaQuery,
  useCreateSpostamentoMutation,
  useUpdateSpostamentoReplyMutation,

  useGetTabelloniQuery,
  useCreateTabelloneMutation,
  useUpdateTabelloneMutation,
  useDeleteTabelloneMutation,
  useCreateTabelloneSfidaMutation,
  useUpdateTabelloneSfidaMutation,
  useDeleteTabelloneSfidaMutation,

  useGetTesseratiQuery,
  useGetTesseratiPlayersByGaraSquadraQuery,
  useGetTesseratiStatsQuery,

  useGetZoneQuery,
  useCreateZonaMutation,
  useUpdateZonaMutation,
  useDeleteZonaMutation,
} = apiSlice
